import { Checkbox, SelectProps } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import classNames from 'classnames';
import { omit } from 'lodash';
import { ReactNode, useState } from 'react';
import { CommonSelect } from '.';
import getFormatMessage from '../../locales/get-format-message';
import { CustomPopover } from '../custom-popover';
import { CommonSearchInput } from '../input';
import styles from './index.module.scss';

type CommonCheckBoxSelectProps = SelectProps & {
  after?: ReactNode;
  maxCount?: number;
  prefix?: string;
  name?: string;
  sortOption?: boolean;
  showSearchInput?: boolean;
  showSelectAll?: boolean;
};

export const CommonCheckBoxSelect = (props: CommonCheckBoxSelectProps) => {
  const {
    options = [],
    value = [],
    showSearchInput = true,
    showSelectAll = true,
  } = props;
  const [searchText, setSearchText] = useState<string>('');
  const otherProps = omit(props, 'options');

  const renderList = (filterOptions: DefaultOptionType[]) => {
    if (!filterOptions.length) {
      return (
        <div
          className={styles.listContainer}
          style={{ padding: 12, color: '#C9CDD4' }}
        >
          {getFormatMessage('no_data_available')}
        </div>
      );
    }
    return (
      <div className={styles.listContainer}>
        {showSelectAll && (
          <div
            className={styles.listItem}
            onClickCapture={() => {
              const hasSelectedAll = value.length === filterOptions.length;
              if (hasSelectedAll) {
                props.onChange?.([], []);
              } else {
                props.onChange?.(
                  filterOptions?.map((item) => item.value) || [],
                  filterOptions
                );
              }
            }}
          >
            <Checkbox
              checked={
                value.length > 0 && value.length === filterOptions?.length
              }
              indeterminate={
                value.length > 0 && value.length < filterOptions?.length
              }
            ></Checkbox>
            <span className={styles.itemContent}>全选</span>
          </div>
        )}
        <div className={styles.selectOptionArea}>
          {filterOptions.map((option) => {
            return (
              <div
                className={styles.listItem}
                onClickCapture={() => {
                  const hasSelected = value.includes(option.value);
                  if (hasSelected) {
                    props.onChange?.(
                      value.filter((item: any) => item !== option.value),
                      option
                    );
                  } else {
                    props.onChange?.([...value, option.value], option);
                  }
                }}
              >
                <Checkbox checked={value.includes(option.value)}></Checkbox>
                <span className={styles.itemContent}>{option.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getDropdown = () => {
    const filterOptions = options?.filter(
      (option) =>
        String(option.label)
          .toLowerCase()
          .includes(searchText?.toLowerCase()) ||
        String(option.value)
          .toLowerCase()
          .includes(searchText?.toLowerCase()) ||
        !searchText
    );

    return (
      <div className={styles.checkboxSelectDropDown}>
        {showSearchInput && (
          <div className={styles.search}>
            <CommonSearchInput
              bordered={false}
              onKeyDown={(ev) => {
                ev.stopPropagation();
              }}
              onChange={(ev) => {
                ev.stopPropagation();
                setSearchText(ev.target.value);
              }}
            />
          </div>
        )}
        {renderList(filterOptions)}
      </div>
    );
  };

  if (props.mode !== 'multiple' && props.mode !== 'tags') {
    return (
      <CommonSelect
        {...otherProps}
        value={value}
        sortOption={props.sortOption}
        options={
          options?.map((option) => {
            return {
              label: (
                <div>
                  <Checkbox
                    checked={value.includes(option.value)}
                    onChange={() => undefined}
                  ></Checkbox>
                  <span>{option.label}</span>
                </div>
              ),
              text: option.label,
              value: option.value,
            };
          }) || []
        }
        optionLabelProp="label"
        className={styles.checkboxSelect}
        getPopupContainer={(node) => node.parentElement}
      />
    );
  }

  const isSelectAll =
    !value ||
    !value.length ||
    (value instanceof Array && value.length === options.length);

  const getMaxPlaceholder = (omittedValues: any[]) => {
    if (isSelectAll) {
      return (
        <div className={styles.allTag}>
          {getFormatMessage('select_all_tag')}
        </div>
      );
    }
    const content = `+${omittedValues.length}`;
    return (
      <CustomPopover
        content={
          value instanceof Array
            ? value
                .map(
                  (valueItem) =>
                    options.find((opt) => opt.value === valueItem)?.label
                )
                .join('、')
            : ''
        }
      >
        {content}
      </CustomPopover>
    );
  };

  return (
    <CommonSelect
      {...otherProps}
      value={isSelectAll && showSelectAll ? 'all' : value}
      allowClear={isSelectAll ? false : props.allowClear}
      sortOption={props.sortOption}
      options={options}
      showSearch={false}
      optionLabelProp="label"
      className={classNames(styles.checkboxSelect, {
        [styles.selectAll]: isSelectAll,
      })}
      getPopupContainer={(node) => node.parentElement}
      dropdownRender={getDropdown}
      maxTagCount={isSelectAll ? 0 : props.maxCount}
      maxTagPlaceholder={getMaxPlaceholder}
    />
  );
};

export const CommonMultiSelect = (props: CommonCheckBoxSelectProps) => {
  return (
    <CommonCheckBoxSelect
      {...props}
      showSearchInput={false}
      showSelectAll={false}
      mode="multiple"
    />
  );
};
